import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { API_ENDPOINTS, getImageUrl } from '../config/api';

const FeaturedGallery = () => {
  const [featuredImages, setFeaturedImages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadFeaturedImages();
  }, []);

  const loadFeaturedImages = async () => {
    try {
      const response = await fetch(API_ENDPOINTS.GALLERY);
      if (response.ok) {
        const data = await response.json();
        // Get 4 random active images for featured section
        const activeImages = data.filter(item => item.isActive && item.fileType === 'image');
        const shuffled = activeImages.sort(() => 0.5 - Math.random());
        const selected = shuffled.slice(0, 4);
        setFeaturedImages(selected);
      }
    } catch (error) {
      console.error('Error loading featured images:', error);
    } finally {
      setLoading(false);
    }
  };

  const categories = [
    { value: 'general', label: 'General' },
    { value: 'events', label: 'Events' },
    { value: 'sports', label: 'Sports' },
    { value: 'academic', label: 'Academic' },
    { value: 'celebrations', label: 'Celebrations' },
    { value: 'facilities', label: 'Facilities' }
  ];

  const getCategoryColor = (category) => {
    const colors = {
      general: 'bg-blue-100 text-blue-800',
      events: 'bg-purple-100 text-purple-800',
      sports: 'bg-green-100 text-green-800',
      academic: 'bg-yellow-100 text-yellow-800',
      celebrations: 'bg-pink-100 text-pink-800',
      facilities: 'bg-indigo-100 text-indigo-800'
    };
    return colors[category] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <section className="py-16 px-6 bg-gray-50">
        <div className="max-w-6xl mx-auto text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary mb-4"></div>
          <p className="text-gray-600">Loading featured moments...</p>
        </div>
      </section>
    );
  }

  if (featuredImages.length === 0) {
    return null;
  }

  return (
    <section className="py-16 px-6 bg-gray-50">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            Featured Moments
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Take a glimpse into the vibrant life at Creative Town Bilingual School.
            These moments capture our community's spirit and achievements.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {featuredImages.map((image) => (
            <div
              key={image.id}
              className="group relative overflow-hidden rounded-xl shadow-lg hover:shadow-xl transition duration-300 transform hover:-translate-y-1"
            >
              <div className="aspect-square overflow-hidden">
                <img
                  src={getImageUrl(image.filePath)}
                  alt={image.title}
                  className="w-full h-full object-cover group-hover:scale-110 transition duration-500"
                />
              </div>

              {/* Overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent opacity-0 group-hover:opacity-80 transition duration-300 flex items-end">
                <div className="p-4 text-white w-full">
                  <h3 className="font-bold text-lg mb-1">{image.title}</h3>
                  <p className="text-sm opacity-90 mb-2">{image.description}</p>
                  <div className="flex items-center justify-between">
                    <span className={`text-xs px-2 py-1 rounded-full ${getCategoryColor(image.category)}`}>
                      {categories.find(c => c.value === image.category)?.label}
                    </span>
                    <div className="flex items-center space-x-2 text-xs">
                      <span>❤️ {image.likes}</span>
                      <span>👁️ {image.views}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Category Badge */}
              <div className="absolute top-3 left-3">
                <span className={`text-xs px-2 py-1 rounded-full ${getCategoryColor(image.category)} shadow-sm`}>
                  {categories.find(c => c.value === image.category)?.label}
                </span>
              </div>
            </div>
          ))}
        </div>

        {/* View All Gallery Button */}
        <div className="text-center">
          <Link
            to="/gallery"
            className="inline-flex items-center px-8 py-3 bg-primary text-white rounded-full font-semibold hover:bg-blue-700 transition duration-300 transform hover:scale-105 shadow-lg"
          >
            <span>View Full Gallery</span>
            <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </Link>
        </div>
      </div>
    </section>
  );
};

export default FeaturedGallery;



